import React from "react";
import ServiceImageSlots from "./ServiceImageSlots";
import { SERVICE_CATEGORIES } from "../constants/serviceCategories";
// import "../pages/CreateService.css";

const ServiceFormFields = ({
  formData,
  setFormData,
  creneaux,
  setCreneaux,
  createEmptyCreneau,
  imageSlots,
  onImageSlotChange,
  onImageSlotRemove,
  maxImages,
  imageInputPrefix,
  isFieldEditable = () => true,
  minNombrePersonnesMaxAtelier = 1,
}) => {
  const canEdit = (fieldName) => isFieldEditable(fieldName);
  const showsLieuField = String(formData.typePrestation || "").toLowerCase().includes("présentiel");
  const getEffectiveCategoryCount = (categories = [], customCategories = []) =>
    (categories || []).filter((category) => category !== "Autres").length +
    (customCategories || []).map((value) => value.trim()).filter(Boolean).length;

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (!canEdit(name)) return;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };

      if (name === "typePrestation") {
        if (!String(value).toLowerCase().includes("présentiel")) {
          next.lieu = "";
          next.ville = "";
          next.adresse = "";
        }
      }

      if (name === "typeCours") {
        if (!String(value).includes("Collective")) {
          next.nombrePlaces = "";
          next.nombrePersonnesMaxAtelier = "";
        }
      }

      if (name === "ville" || name === "adresse") {
        const nextVille = name === "ville" ? value : prev.ville || "";
        const nextAdresse = name === "adresse" ? value : prev.adresse || "";
        next.lieu = [nextVille, nextAdresse].filter(Boolean).join(" - ");
      }

      if (name === "accessiblePMR" && !checked) {
        next.pmrDetails = "";
      }

      return next;
    });
  };

  const handleCategoryToggle = (category) => {
    if (!canEdit("categories")) return;
    setFormData((prev) => {
      if (category === "Autres") {
        const hasAutres = prev.categories.includes("Autres");
        if (hasAutres) {
          return {
            ...prev,
            categories: prev.categories.filter((c) => c !== "Autres"),
          };
        }

        if (getEffectiveCategoryCount(prev.categories, prev.autresCategories) >= 10) {
          return prev;
        }

        return {
          ...prev,
          categories: [...prev.categories, "Autres"],
          autresCategories: prev.autresCategories?.length ? prev.autresCategories : [""],
        };
      }

      if (prev.categories.includes(category)) {
        return {
          ...prev,
          categories: prev.categories.filter((c) => c !== category),
        };
      }

      if (getEffectiveCategoryCount([...prev.categories, category], prev.autresCategories) > 10) {
        return prev;
      }

      return {
        ...prev,
        categories: [...prev.categories, category],
      };
    });
  };

  const addCustomCategory = () => {
    setFormData((prev) => {
      if (getEffectiveCategoryCount(prev.categories, prev.autresCategories) >= 10) {
        return prev;
      }
      return {
        ...prev,
        autresCategories: [...(prev.autresCategories || []), ""],
      };
    });
  };

  const updateCustomCategory = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      autresCategories: (prev.autresCategories || []).map((item, idx) => idx === index ? value : item),
    }));
  };

  const removeCustomCategory = (index) => {
    setFormData((prev) => ({
      ...prev,
      autresCategories: (prev.autresCategories || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleCreneauChange = (index, field, value) => {
    const creneauField = field === "date" ? "dateService" : "heure";
    if (!canEdit(creneauField)) return;
    setCreneaux((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const handleDateAConvenirToggle = (checked) => {
    if (!canEdit("dateAConvenir")) return;
    setFormData((prev) => ({
      ...prev,
      dateAConvenir: checked,
      dateService: checked ? [] : prev.dateService,
      heure: checked ? "" : prev.heure,
    }));

    if (checked) {
      setCreneaux([createEmptyCreneau()]);
    }
  };

  return (
    <>
      <div>
        <label className="block text-gray-700 font-medium mb-1">Titre du service*</label>
        <input
          type="text"
          name="titre"
          value={formData.titre}
          onChange={handleFieldChange}
          placeholder="Ex: Atelier de méditation guidée"
          className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("titre")}
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleFieldChange}
          rows={4}
          placeholder="Décrivez votre service..."
          className="w-full border border-gray-300 px-4 py-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("description")}
          required
        />
      </div>

      <div className={!canEdit("categories") ? "opacity-50" : ""}>
        <label className="block text-gray-700 font-medium mb-2">Catégories *</label>
        <p className="text-xs text-gray-500 mb-2">Choisissez jusqu’à 10 catégories maximum.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SERVICE_CATEGORIES.map((cat, index) => {
            const catId = `category-${index}`;
            const isChecked = formData.categories.includes(cat.nom);
            return (
              <div key={cat.nom} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={catId}
                  value={cat.nom}
                  checked={isChecked}
                  onChange={() => handleCategoryToggle(cat.nom)}
                  className="w-4 h-4 border-gray-300 rounded accent-[var(--accent-primary)] focus:ring-[var(--accent-primary)]"
                  disabled={!canEdit("categories")}
                />
                <label
                  htmlFor={catId}
                  className={`text-sm ${isChecked ? "text-[var(--accent-primary)] font-medium" : "text-gray-700"}`}
                >
                  {cat.nom}
                </label>
              </div>
            );
          })}
        </div>

        {formData.categories.includes("Autres") && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-gray-700 text-sm font-medium">Catégories libres supplémentaires</label>
              <button
                type="button"
                onClick={addCustomCategory}
                className="text-xs text-[var(--accent-primary)] hover:underline"
              >
                + Ajouter
              </button>
            </div>

            {(formData.autresCategories || []).map((value, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={value}
                  onChange={(e) => updateCustomCategory(index, e.target.value)}
                  placeholder="Ex : Coaching, Photographie..."
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
                  disabled={!canEdit("categories")}
                />
                <button
                  type="button"
                  onClick={() => removeCustomCategory(index)}
                  className="text-sm text-gray-500 hover:text-red-600"
                >
                  Retirer
                </button>
              </div>
            ))}

            {(!formData.autresCategories || formData.autresCategories.length === 0) && (
              <button
                type="button"
                onClick={addCustomCategory}
                className="text-sm text-[var(--accent-primary)] hover:underline"
              >
                Ajouter une catégorie "Autres"
              </button>
            )}
          </div>
        )}

        {formData.categories.length === 0 && (
          <p className="text-red-500 text-sm mt-1">Veuillez sélectionner au moins une catégorie</p>
        )}
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">Type de prestation</label>
        <select
          name="typePrestation"
          value={formData.typePrestation}
          onChange={handleFieldChange}
          className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("typePrestation")}
        >
          <option value="">Sélectionnez</option>
          <option value="En ligne">Distanciel</option>
          <option value="Présentiel">Présentiel</option>
          <option value="Distanciel & Présentiel">Distanciel & Présentiel</option>
        </select>
      </div>

      {showsLieuField && (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Genève ou Paris</label>
            <select
              name="ville"
              value={formData.ville || ""}
              onChange={handleFieldChange}
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
              disabled={!canEdit("ville")}
            >
              <option value="">Sélectionnez</option>
              <option value="Genève">Genève</option>
              <option value="Paris">Paris</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">Adresse</label>
            <input
              type="text"
              name="adresse"
              value={formData.adresse || ""}
              onChange={handleFieldChange}
              placeholder="Ex : 12 rue de la Paix"
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
              disabled={!canEdit("adresse")}
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-gray-700 font-medium mb-1">Valeur en crédits par participant *</label>
        <input
          type="number"
          name="creditsProposes"
          value={formData.creditsProposes || ""}
          onChange={handleFieldChange}
          placeholder="Ex: 50 crédits"
          min="1"
          step="1"
          className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("creditsProposes")}
          required
        />
      </div>

      {!formData.dateAConvenir && (
        <div className={(!canEdit("dateService") || !canEdit("heure")) ? "opacity-50" : ""}>
          <label className="block text-gray-700 font-medium mb-2">Créneau disponible</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-700 text-sm mb-1">Date</label>
              <input
                type="date"
                value={creneaux[0]?.date || ""}
                onChange={(e) => handleCreneauChange(0, "date", e.target.value)}
                className="w-full border border-gray-300 px-4 py-3 rounded-lg"
                disabled={!canEdit("dateService")}
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm mb-1">Heure</label>
              <input
                type="time"
                value={creneaux[0]?.heure || ""}
                onChange={(e) => handleCreneauChange(0, "heure", e.target.value)}
                className="w-full border border-gray-300 px-4 py-3 rounded-lg"
                disabled={!canEdit("heure")}
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-gray-700 font-medium mb-1">Durée</label>
        <input
          type="text"
          name="duree"
          value={formData.duree}
          onChange={handleFieldChange}
          placeholder="ex: 1h30"
          className="w-full border border-gray-300 px-4 py-3 rounded-lg"
          disabled={!canEdit("duree")}
        />
      </div>

      <div className={`flex items-center gap-3 ${!canEdit("dateAConvenir") ? "opacity-50" : ""}`}>
        <input
          type="checkbox"
          id={`${imageInputPrefix}-date-convenir`}
          name="dateAConvenir"
          checked={formData.dateAConvenir}
          onChange={(e) => handleDateAConvenirToggle(e.target.checked)}
          disabled={!canEdit("dateAConvenir")}
        />

        <label htmlFor={`${imageInputPrefix}-date-convenir`} className="text-gray-700 font-medium">
          Date et heure de rdv à convenir ensemble
        </label>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">Type de séance</label>
        <select
          name="typeCours"
          value={formData.typeCours}
          onChange={handleFieldChange}
          className="w-full border border-gray-300 px-4 py-3 rounded-lg"
          disabled={!canEdit("typeCours")}
        >
          <option value="">Sélectionnez</option>
          <option value="Individuelle">Individuelle</option>
          <option value="Collective">Collective</option>
          <option value="Individuelle & Collective">Individuelle & Collective</option>
        </select>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">Public cible</label>
        <input
          type="text"
          name="publicCible"
          value={formData.publicCible || ""}
          onChange={handleFieldChange}
          placeholder="Ex : Adultes, adolescents, parents-enfants..."
          className="w-full border border-gray-300 px-4 py-3 rounded-lg"
          disabled={!canEdit("publicCible")}
        />
      </div>

      {(formData.typeCours === "Collective" || formData.typeCours === "Individuelle & Collective") && (
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Nombre de places disponibles</label>
            <input
              type="number"
              name="nombrePlaces"
              value={formData.nombrePlaces || ""}
              onChange={handleFieldChange}
              min={minNombrePersonnesMaxAtelier}
              step="1"
              placeholder="Ex: 12"
              className="w-full border border-gray-300 px-4 py-3 rounded-lg"
              disabled={!canEdit("nombrePlaces")}
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">Capacité maximale</label>
            <input
              type="number"
              name="capaciteMaximale"
              value={formData.capaciteMaximale || ""}
              onChange={handleFieldChange}
              min="1"
              step="1"
              placeholder="Ex: 20"
              className="w-full border border-gray-300 px-4 py-3 rounded-lg"
              disabled={!canEdit("capaciteMaximale")}
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-gray-700 font-medium mb-1">Prérequis</label>
        <textarea
          name="prerequis"
          value={formData.prerequis}
          onChange={handleFieldChange}
          rows={2}
          placeholder="Ex : aucun, bases recommandées, motivation requise…"
          className="w-full border border-gray-300 px-4 py-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("prerequis")}
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">Matériel nécessaire</label>
        <textarea
          name="materiel"
          value={formData.materiel}
          onChange={handleFieldChange}
          rows={2}
          placeholder="Ex : ordinateur, connexion internet, carnet…"
          className="w-full border border-gray-300 px-4 py-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("materiel")}
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id={`${imageInputPrefix}-accessible-pmr`}
          name="accessiblePMR"
          checked={formData.accessiblePMR}
          onChange={handleFieldChange}
          className="w-5 h-5 text-[var(--status-success)] border-gray-300 rounded focus:ring-[var(--accent-primary)]"
          disabled={!canEdit("accessiblePMR")}
        />
        <label htmlFor={`${imageInputPrefix}-accessible-pmr`} className="text-gray-700 font-medium">
          Accessible PMR (Personne à mobilité réduite)
        </label>
      </div>

      {formData.accessiblePMR && (
        <div className="mt-3">
          <label className="block text-gray-700 font-medium mb-1">
            Information sur l’accessibilité du service ou du lieu
          </label>
          <input
            type="text"
            name="pmrDetails"
            value={formData.pmrDetails}
            onChange={handleFieldChange}
            placeholder="Ex : rampe d’accès, ascenseur, toilettes adaptées…"
            className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
            disabled={!canEdit("pmrDetails")}
          />
        </div>
      )}

      <div className="rounded-xl recoPhoto border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
        <p className="font-medium mb-1">Recommandations photo</p>
        <p>Format accepté : JPG, PNG, WebP</p>
        <p>Dimensions recommandées : 1200 × 800 px minimum (ratio 3:2)</p>
        <p>Poids maximum : 5 Mo</p>
      </div>

      <ServiceImageSlots
        imageSlots={imageSlots}
        onChangeSlot={onImageSlotChange}
        onRemoveSlot={onImageSlotRemove}
        maxImages={maxImages}
        inputPrefix={imageInputPrefix}
        disabled={!canEdit("images")}
      />
    </>
  );
};

export default ServiceFormFields;
